import React from "react";
import { Box, Grid, IconButton, Link, Typography, Button } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningIcon from "@mui/icons-material/Warning";
import DoNotDisturbOnIcon from "@mui/icons-material/DoNotDisturbOn";
import SubdirectoryArrowLeftIcon from "@mui/icons-material/SubdirectoryArrowLeft";
import RemoveIcon from "@mui/icons-material/Remove";
import AddIcon from "@mui/icons-material/Add";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import { SouthAfricanRand } from "../../utils/helpers";
import { COLORS, FONT } from "../../constants";
import { AccountType, ProductAvailability } from "../../utils/constants";
import ProductImageGallery from "./ProductImageGallery";

const MobileProductInfo = ({
  product,
  productPrice,
  accountType,
  availabilityValue,
  quantity,
  setQuantity,
  handleAddToCartClick,
}) => {
  return (
    <Grid
      container
      spacing={2}
      sx={{ flexDirection: { xs: "column", md: "row" } }}
    >
      <Grid item xs={12} md={8.7}>
        <Grid
          container
          xs={12}
          sx={{
            backgroundColor: COLORS.white,
            padding: 2,
            border: `1px solid ${COLORS.border}`,
            borderRadius: "2px",
          }}
        >
          {/* Left Grid Item: Product Image */}
          <Grid
            item
            xs={12}
            md={6}
            sx={{
              display: "flex",
              justifyContent: "center",
              border: `1px solid ${COLORS.border}`,
              borderRadius: "2px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                width: "100%",
              }}
            >
              <ProductImageGallery product={product} />
            </Box>
          </Grid>

          {/* Right Grid Item: Product Details */}
          <Grid item xs={12} md={6}>
            <Box sx={{ display: "block" }}>
              <Typography
                variant="caption"
                sx={{
                  mt: 2,
                  mb: 1,
                  display: "inline-flex",
                  fontWeight: 300,
                  color: COLORS.themeBlack,
                }}
              >
                SKU:{" "}
                <Typography variant="caption" sx={{ fontWeight: 500 }}>
                  {" "}
                  {product.sku}
                </Typography>
              </Typography>
            </Box>
            <Box sx={{ display: "block" }}>
              <Typography
                variant="h6"
                sx={{ fontSize: "var(--font-size-m)", fontWeight: 500 }}
              >
                {product.name}
              </Typography>
            </Box>
            {!!productPrice && (
              <Box
                sx={{
                  marginTop: 2,
                  marginBottom: 3,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                }}
              >
                {productPrice.promo > 0 && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Typography
                      color={
                        productPrice.promo < 0
                          ? COLORS.themeRed
                          : COLORS.lightText
                      }
                      sx={{
                        fontWeight: "500",
                        textDecoration: "line-through",
                        fontSize: "var(--font-size-xs)",
                      }}
                    >
                      {SouthAfricanRand.format(productPrice.price)}
                    </Typography>
                    {accountType === AccountType.Commercial && (
                      <Typography variant="subtitle1" color="textSecondary">
                        (Ex. VAT)
                      </Typography>
                    )}
                  </Box>
                )}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Typography
                      color={productPrice.promo > 0 ? "error" : COLORS.themeRed}
                      sx={{
                        fontWeight: "500",
                        fontSize:
                          productPrice.promo > 0
                            ? "var(--font-size-xl)"
                            : "inherit",
                      }}
                    >
                      {SouthAfricanRand.format(
                        productPrice.promo > 0
                          ? productPrice.promo
                          : productPrice.price,
                      )}
                    </Typography>
                    {accountType === AccountType.Commercial &&
                      productPrice.promo === 0 && (
                        <Typography variant="subtitle1" color="textSecondary">
                          (Ex. VAT)
                        </Typography>
                      )}
                  </Box>
                  {availabilityValue && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        marginLeft: "1rem",
                      }}
                    >
                      {availabilityValue === ProductAvailability.InStock && (
                        <CheckCircleIcon
                          sx={{
                            color: COLORS.themeGreen,
                            fontSize: "1rem",
                          }}
                        />
                      )}
                      {availabilityValue === ProductAvailability.LowStock && (
                        <WarningIcon
                          sx={{
                            color: COLORS.themeOrange,
                            fontSize: "1rem",
                          }}
                        />
                      )}
                      {availabilityValue === ProductAvailability.No_Stock && (
                        <DoNotDisturbOnIcon
                          sx={{
                            color: COLORS.themeRed,
                            fontSize: "1rem",
                          }}
                        />
                      )}
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: "0.875rem",
                          color:
                            availabilityValue === ProductAvailability.InStock
                              ? COLORS.themeGreen
                              : availabilityValue ===
                                  ProductAvailability.LowStock
                                ? COLORS.themeOrange
                                : COLORS.themeRed,
                        }}
                      >
                        {availabilityValue}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            )}
            {product.vendor && (
              <Box>
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "0.25rem",
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: COLORS.darkText,
                      fontSize: "var(--font-size-xxs)",
                      fontWeight: 300,
                    }}
                  >
                    VENDOR:
                  </Typography>
                  <Link
                    href={`/products/all?vendor=${product.vendorId}`}
                    variant="caption"
                    sx={{
                      textDecoration: "none",
                      fontSize: "var(--font-size-xxs)",
                      fontWeight: 500,
                      textTransform: "uppercase",
                    }}
                  >
                    {product.vendor}
                  </Link>
                </Box>
              </Box>
            )}
            {product.quality && (
              <Box>
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "0.25rem",
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: COLORS.darkText,
                      fontSize: "var(--font-size-xxs)",
                      fontWeight: 300,
                      padding: "0.2rem 0 0.2rem 0",
                    }}
                  >
                    QUALITY:
                  </Typography>
                  <Link
                    href={`/products/all?vendor=${product.vendorId}`}
                    variant="caption"
                    sx={{
                      textDecoration: "none",
                      fontSize: "var(--font-size-xxs)",
                      fontWeight: 500,
                      padding: "0.2rem 0 0.2rem 0",
                    }}
                  >
                    {product.quality}
                  </Link>
                </Box>
              </Box>
            )}
            {!!product?.productCategory && !!product?.productType && (
              <Box>
                <Box sx={{ display: "inline-flex", gap: "0.25rem" }}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: COLORS.darkText,
                      fontSize: "var(--font-size-xxs)",
                      fontWeight: 300,
                      padding: "0.2rem 0 0.2rem 0",
                    }}
                  >
                    TYPE:
                  </Typography>
                  <Link
                    href={`/products/${product.productCategoryId}/${product.productTypeId}`}
                    variant="caption"
                    sx={{
                      textDecoration: "none",
                      fontSize: "var(--font-size-xxs)",
                      fontWeight: 500,
                      padding: "0.2rem 0 0.2rem 0",
                      textTransform: "capitalize",
                    }}
                  >
                    {product.productType.replaceAll("_", " ")}
                  </Link>
                </Box>
              </Box>
            )}
            {!!product.productCategory && (
              <Box>
                <Box sx={{ display: "block" }}>
                  <Box>
                    <Box
                      sx={{
                        display: "inline-flex",
                        gap: "0.25rem",
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{
                          color: COLORS.darkText,
                          fontSize: "var(--font-size-xxs)",
                          fontWeight: 300,
                          padding: "0.2rem 0 0.2rem 0",
                        }}
                      >
                        CATEGORY:
                      </Typography>
                      <SubdirectoryArrowLeftIcon
                        sx={{
                          transform: "rotate(-90deg)",
                          fontSize: "1.5rem",
                          mt: 1,
                          color: COLORS.themeBlue,
                        }}
                      />
                    </Box>
                  </Box>
                  <Link
                    href={`/products/${product.productCategoryId}`}
                    variant="caption"
                    sx={{
                      display: "inline-flex",
                      border: `1px solid ${COLORS.themeBlue}`,
                      fontWeight: 500,
                      textDecoration: "none",
                      padding: "0.4rem 1rem 0.4rem 1rem",
                      borderRadius: "2px",
                      textTransform: "capitalize",
                    }}
                  >
                    {product.productCategory.replaceAll("_", " ")}
                  </Link>
                </Box>
              </Box>
            )}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mt: 1,
                py: 2,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "2px",
                  backgroundColor: COLORS.background,
                  padding: "0.45em 0.25rem",
                  width: "8rem",
                }}
              >
                <IconButton
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  disabled={
                    availabilityValue === ProductAvailability.No_Stock ||
                    quantity <= 1
                  }
                  sx={{
                    flex: 1,
                    display: "flex",
                    justifyContent: "center",
                    padding: 0,
                    color: COLORS.gray,
                  }}
                >
                  <RemoveIcon fontSize="small" />
                </IconButton>
                <Typography
                  sx={{
                    flex: 1,
                    textAlign: "center",
                    padding: "0 0.5rem",
                    color: COLORS.darkText,
                  }}
                >
                  {quantity}
                </Typography>
                <IconButton
                  onClick={() => setQuantity(Math.min(99, quantity + 1))}
                  disabled={
                    availabilityValue === ProductAvailability.No_Stock ||
                    quantity >= 99
                  }
                  sx={{
                    flex: 1,
                    display: "flex",
                    justifyContent: "center",
                    padding: 0,
                    color: COLORS.gray,
                  }}
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              </Box>
              <Button
                variant="contained"
                color="primary"
                size="small"
                fullWidth
                onClick={() => {
                  handleAddToCartClick();
                }}
                disabled={
                  availabilityValue === ProductAvailability.No_Stock ||
                  quantity < 1
                }
                sx={{
                  backgroundColor: COLORS.themeYellow,
                  color: COLORS.darkText,
                  padding: "0.5rem 0.2rem 0.5rem 0.2rem",
                  borderRadius: "2px",
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: COLORS.themeYellow,
                    opacity: 0.9,
                  },
                  "&.Mui-disabled": {
                    pointerEvents: "auto",
                    cursor: "not-allowed",
                  },
                }}
              >
                <ShoppingCartIcon sx={{ color: COLORS.darkText, mr: 0.5 }} />
                Add to Cart
              </Button>
            </Box>

            {/* Need Help */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                mt: 2,
                p: 2,
                backgroundColor: COLORS.background,
                borderRadius: "2px",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "2rem",
                  height: "2rem",
                  border: `2px solid ${COLORS.darkText}`,
                  borderRadius: "50%",
                  flexShrink: 0,
                }}
              >
                <Typography sx={{ fontWeight: 700, fontSize: FONT.size.xs, color: COLORS.darkText }}>
                  ?
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" sx={{ color: COLORS.darkText, lineHeight: 1.4, fontSize: FONT.size.xs }}>
                  <Box component="span" sx={{ fontWeight: 700 }}>Need Help?</Box>{" "}
                  Call Us{" "}
                  <Box component="span" sx={{ fontWeight: 700 }}>011 747 5200</Box>
                </Typography>
                <Typography variant="body2" sx={{ color: COLORS.lightText, lineHeight: 1.4, fontSize: FONT.size.xs }}>
                  Mon–Thur 07:30 – 16:30
                </Typography>
                <Typography variant="body2" sx={{ color: COLORS.lightText, lineHeight: 1.4, fontSize: FONT.size.xs }}>
                  Fri 07:30 – 14:00
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default MobileProductInfo;
